import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { questionnaireService } from '../services/api';
import { toast } from 'sonner';
import { HelpCircle, Filter, Plus, X, Sparkles, Edit } from 'lucide-react';
import { api } from '../services/api';
import { AutocompleteInput } from '../components/AutocompleteInput';

export const QuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    fonction: '',
    niveau: '',
    entreprise_cliente: '',
    interlocuteur_client: '',
    ref_ao: '',
    tag: '',
    is_client_interview: false,
    evaluateur: ''
  });

  // Create question state
  const [isClientInterview, setIsClientInterview] = useState(false);
  const [createMode, setCreateMode] = useState('ai'); // 'ai' or 'manual'
  const [generationMode, setGenerationMode] = useState('full'); // 'full' or 'answer_only'
  const [questionData, setQuestionData] = useState({
    entreprise_cliente: '',
    interlocuteur_client: '',
    niveau: '',
    competence: '',
    question_text: '',
    expected_answer: [],
    tags: []
  });
  const [generatedData, setGeneratedData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [rephrasing, setRephrasing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, [filters]);

  const fetchQuestions = async () => {
    try {
      const response = await questionnaireService.getAllQuestions(filters);
      setQuestions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuestion = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/questions/generate', {
        entreprise_cliente: questionData.entreprise_cliente || null,
        interlocuteur_client: questionData.interlocuteur_client || null,
        niveau: questionData.niveau || null,
        competence: questionData.competence || null,
        generate_mode: generationMode,
        question_text: generationMode === 'answer_only' ? questionData.question_text : null
      });

      setGeneratedData(response.data);
      toast.success(generationMode === 'full' ? 'Question générée' : 'Réponse générée');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!generatedData) {
      toast.error('Veuillez générer une question d\'abord');
      return;
    }

    // Validate if client interview
    if (isClientInterview && (!questionData.entreprise_cliente || !questionData.interlocuteur_client)) {
      toast.error('Entreprise cliente et Opérationnel sont obligatoires pour une question d\'entretien client');
      return;
    }

    setSaving(true);
    try {
      const isQcm = generatedData.type === 'qcm' || (generatedData.choices && generatedData.choices.length > 0);
      await api.post('/questions', {
        text: generatedData.text,
        expected_answer: generatedData.expected_answer || [],
        choices: generatedData.choices || [],
        type: isQcm ? 'qcm' : 'ouverte',
        entreprise_cliente: questionData.entreprise_cliente || null,
        interlocuteur_client: questionData.interlocuteur_client || null,
        niveau: questionData.niveau || null,
        competence: questionData.competence || null,
        tags: generatedData.tags || [],
        is_client_interview: isClientInterview
      });

      toast.success('Question créée avec succès');
      setShowCreateModal(false);
      resetCreateForm();
      fetchQuestions();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleRephraseQuestion = async () => {
    if (!questionData.question_text.trim()) {
      toast.error('Veuillez entrer une question à reformuler');
      return;
    }

    setRephrasing(true);
    try {
      const response = await api.post('/questions/rephrase', {
        question_text: questionData.question_text,
        competence: questionData.competence || null
      });

      setQuestionData({ ...questionData, question_text: response.data.rephrased_text });
      toast.success('Question reformulée');
    } catch (error) {
      toast.error('Erreur lors de la reformulation');
    } finally {
      setRephrasing(false);
    }
  };

  const resetCreateForm = () => {
    setQuestionData({
      entreprise_cliente: '',
      interlocuteur_client: '',
      niveau: '',
      competence: '',
      question_text: '',
      expected_answer: [],
      tags: []
    });
    setGeneratedData(null);
    setCreateMode('ai');
    setGenerationMode('full');
    setIsClientInterview(false);
  };

  return (
    <Layout title="Questions" breadcrumbs={['Accueil', 'Questions']}>
      <div className="mb-6">
        <button
          onClick={() => setShowCreateModal(true)}
          className="eva-btn-primary flex items-center gap-2"
          data-testid="create-question-button"
        >
          <Plus size={18} />
          Créer une nouvelle question
        </button>
      </div>

      {/* Filters */}
      <div className="eva-card mb-6" data-testid="filters">
        <div className="flex items-center gap-3 mb-4">
          <Filter size={18} className="text-slate-400" />
          <h3 className="font-semibold text-white">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="eva-label">Poste</label>
            <AutocompleteInput
              value={filters.fonction}
              onChange={(val) => setFilters({ ...filters, fonction: val })}
              endpoint="/autocomplete/fonctions"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-fonction"
            />
          </div>
          <div>
            <label className="eva-label">Niveau</label>
            <select
              value={filters.niveau}
              onChange={(e) => setFilters({ ...filters, niveau: e.target.value })}
              className="eva-input"
              data-testid="filter-niveau"
            >
              <option value="">Tous</option>
              <option value="Junior">Junior</option>
              <option value="Confirmé">Confirmé</option>
              <option value="Senior">Senior</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
          <div>
            <label className="eva-label">Entreprise</label>
            <AutocompleteInput
              value={filters.entreprise_cliente}
              onChange={(val) => setFilters({ ...filters, entreprise_cliente: val })}
              endpoint="/autocomplete/entreprises"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-entreprise"
            />
          </div>
          <div>
            <label className="eva-label">Opérationnel</label>
            <AutocompleteInput
              value={filters.interlocuteur_client}
              onChange={(val) => setFilters({ ...filters, interlocuteur_client: val })}
              endpoint="/autocomplete/interlocuteurs"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-interlocuteur"
            />
          </div>
          <div>
            <label className="eva-label">Tag technique</label>
            <AutocompleteInput
              value={filters.tag}
              onChange={(val) => setFilters({ ...filters, tag: val })}
              endpoint="/autocomplete/tags"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-tag"
            />
          </div>
          <div>
            <label className="eva-label">Réf. AO</label>
            <AutocompleteInput
              value={filters.ref_ao}
              onChange={(val) => setFilters({ ...filters, ref_ao: val })}
              endpoint="/autocomplete/ref_ao"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-ref-ao"
            />
          </div>
          <div>
            <label className="eva-label">Type de question</label>
            <select
              value={filters.is_client_interview ? 'client' : ''}
              onChange={(e) => setFilters({ ...filters, is_client_interview: e.target.value === 'client' })}
              className="eva-input"
              data-testid="filter-client-interview"
            >
              <option value="">Toutes</option>
              <option value="client">Questions posées en entretien client</option>
            </select>
          </div>
          <div>
            <label className="eva-label">Créé par</label>
            <AutocompleteInput
              value={filters.evaluateur}
              onChange={(val) => setFilters({ ...filters, evaluateur: val })}
              endpoint="/autocomplete/evaluateurs"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-evaluateur"
            />
          </div>
        </div>
      </div>

      {/* Questions List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      ) : questions.length === 0 ? (
        <div className="text-center py-12" data-testid="empty-state">
          <HelpCircle size={48} className="text-[#E9ECEF] mx-auto mb-4" />
          <p className="text-slate-400">Aucune question trouvée</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="questions-list">
          {questions.map((q, index) => (
            <div key={index} className="eva-card" data-testid={`question-${index}`}>
              <div className="mb-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white">{q.text}</p>
                  </div>
                  {(q.type === 'qcm' || (q.choices && q.choices.length > 0)) && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#3B82F6]/10 text-[#3B82F6] shrink-0 ml-2">QCM</span>
                  )}
                </div>
                {(q.fonction || q.niveau || q.entreprise_cliente || q.interlocuteur_client) && (
                  <div className="flex gap-2 text-xs text-slate-400 mb-3">
                    {q.fonction && <span>{q.fonction}</span>}
                    {q.fonction && q.niveau && <span>•</span>}
                    {q.niveau && <span>{q.niveau}</span>}
                    {(q.fonction || q.niveau) && (q.entreprise_cliente || q.interlocuteur_client) && <span>•</span>}
                    {q.entreprise_cliente && <span>{q.entreprise_cliente}</span>}
                    {q.entreprise_cliente && q.interlocuteur_client && <span>•</span>}
                    {q.interlocuteur_client && <span>{q.interlocuteur_client}</span>}
                  </div>
                )}
              </div>

              {/* QCM Choices */}
              {q.choices && q.choices.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                    Choix de réponse
                  </p>
                  <ul className="space-y-1">
                    {q.choices.map((choice, i) => (
                      <li key={i} className={`text-sm flex items-start gap-2 ${
                        choice.is_correct ? 'text-[#10B981]' : 'text-slate-300'
                      }`}>
                        <span className="mt-0.5">{choice.is_correct ? '✓' : '○'}</span>
                        {choice.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Open question expected answer */}
              {(!q.choices || q.choices.length === 0) && q.expected_answer && q.expected_answer.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                    Réponse attendue
                  </p>
                  <ul className="space-y-1">
                    {q.expected_answer.map((answer, i) => (
                      <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                        <span className="text-[#10B981] mt-0.5">✓</span>
                        {answer}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {q.tags && q.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {q.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-white/5 text-xs text-slate-400 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Question Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="create-question-modal">
          <div className="bg-[#0B132B] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="sticky top-0 bg-[#0B132B] border-b border-white/10 p-6 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white">Créer une nouvelle question</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="text-slate-400 hover:text-white"
                data-testid="close-modal-button"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              {/* Client Interview Checkbox */}
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isClientInterview}
                    onChange={(e) => {
                      setIsClientInterview(e.target.checked);
                      if (e.target.checked) {
                        setCreateMode('manual');
                        setGenerationMode('answer_only');
                      }
                      setGeneratedData(null);
                    }}
                    className="w-4 h-4"
                    data-testid="client-interview-checkbox"
                  />
                  <span className="text-sm font-medium text-white">Question posée en entretien client</span>
                </label>
                {isClientInterview && (
                  <p className="text-xs text-slate-400 mt-2 ml-6">
                    Les champs "Entreprise cliente" et "Opérationnel" seront obligatoires
                  </p>
                )}
              </div>

              {/* Context Fields */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-white mb-4">
                  Contexte {isClientInterview ? '' : '(optionnel)'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="eva-label">
                      Entreprise cliente {isClientInterview && '*'}
                    </label>
                    <AutocompleteInput
                      value={questionData.entreprise_cliente}
                      onChange={(val) => setQuestionData({ ...questionData, entreprise_cliente: val })}
                      endpoint="/autocomplete/entreprises"
                      className={`eva-input ${isClientInterview && !questionData.entreprise_cliente ? 'border-red-300' : ''}`}
                      dataTestId="modal-entreprise-input"
                    />
                  </div>
                  <div>
                    <label className="eva-label">
                      Opérationnel {isClientInterview && '*'}
                    </label>
                    <AutocompleteInput
                      value={questionData.interlocuteur_client}
                      onChange={(val) => setQuestionData({ ...questionData, interlocuteur_client: val })}
                      endpoint="/autocomplete/interlocuteurs"
                      className={`eva-input ${isClientInterview && !questionData.interlocuteur_client ? 'border-red-300' : ''}`}
                      dataTestId="modal-interlocuteur-input"
                    />
                  </div>
                  <div>
                    <label className="eva-label">Niveau de séniorité</label>
                    <select
                      value={questionData.niveau}
                      onChange={(e) => setQuestionData({ ...questionData, niveau: e.target.value })}
                      className="eva-input"
                      data-testid="modal-niveau-select"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Junior">Junior</option>
                      <option value="Confirmé">Confirmé</option>
                      <option value="Senior">Senior</option>
                      <option value="Expert">Expert</option>
                    </select>
                  </div>
                  <div>
                    <label className="eva-label">Compétence testée</label>
                    <input
                      type="text"
                      value={questionData.competence}
                      onChange={(e) => setQuestionData({ ...questionData, competence: e.target.value })}
                      className="eva-input"
                      placeholder="Ex: React, Python, SQL..."
                      data-testid="modal-competence-input"
                    />
                  </div>
                </div>
              </div>

              {/* Generation Mode - Only show if NOT client interview */}
              {!isClientInterview && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-4">Mode de création</h4>
                  <div className="flex gap-4 mb-4">
                    <button
                      onClick={() => {
                        setCreateMode('ai');
                        setGenerationMode('full');
                        setGeneratedData(null);
                      }}
                      className={`flex-1 p-4 border rounded-lg transition-all ${
                        createMode === 'ai' ? 'border-[#0B132B] bg-white/5' : 'border-white/10'
                      }`}
                      data-testid="mode-ai-button"
                    >
                      <Sparkles size={20} className="text-white mb-2" />
                      <p className="font-medium text-white">Question générée par IA</p>
                      <p className="text-xs text-slate-400 mt-1">L'IA génère la question complète</p>
                    </button>

                    <button
                      onClick={() => {
                        setCreateMode('manual');
                        setGenerationMode('answer_only');
                        setGeneratedData(null);
                      }}
                      className={`flex-1 p-4 border rounded-lg transition-all ${
                        createMode === 'manual' ? 'border-[#0B132B] bg-white/5' : 'border-white/10'
                      }`}
                      data-testid="mode-manual-button"
                    >
                      <Edit size={20} className="text-white mb-2" />
                      <p className="font-medium text-white">Question manuelle</p>
                      <p className="text-xs text-slate-400 mt-1">Vous écrivez, l'IA génère la réponse</p>
                    </button>
                  </div>
                </div>
              )}

              {/* Manual Input (if manual mode OR client interview) */}
              {(createMode === 'manual' || isClientInterview) && (
                <div className="mb-6">
                  <label className="eva-label">Texte de la question *</label>
                  <textarea
                    value={questionData.question_text}
                    onChange={(e) => setQuestionData({ ...questionData, question_text: e.target.value })}
                    className="eva-input"
                    rows={3}
                    placeholder="Écrivez votre question ici..."
                    data-testid="modal-question-textarea"
                  />
                  {isClientInterview && questionData.question_text.trim() && (
                    <button
                      onClick={handleRephraseQuestion}
                      disabled={rephrasing}
                      className="eva-btn-secondary mt-2 text-sm"
                      data-testid="rephrase-button"
                    >
                      {rephrasing ? 'Reformulation...' : 'Reformuler avec l\'IA'}
                    </button>
                  )}
                </div>
              )}

              {/* Generate Button */}
              {!generatedData && (
                <button
                  onClick={handleGenerateQuestion}
                  disabled={generating || (createMode === 'manual' && !questionData.question_text.trim())}
                  className="eva-btn-primary w-full mb-6"
                  data-testid="modal-generate-button"
                >
                  {generating ? 'Génération en cours...' : createMode === 'ai' ? 'Générer la question' : 'Générer la réponse attendue'}
                </button>
              )}

              {/* Generated Result */}
              {generatedData && (
                <div className="mb-6">
                  <div className="eva-card bg-white/5">
                    <h4 className="text-sm font-semibold text-white mb-3">Résultat généré</h4>
                    
                    <div className="mb-4">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                        Question
                      </p>
                      <p className="text-white">{generatedData.text}</p>
                    </div>

                    {/* QCM Choices */}
                    {generatedData.choices && generatedData.choices.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                          Choix de réponse
                        </p>
                        <ul className="space-y-1">
                          {generatedData.choices.map((choice, i) => (
                            <li key={i} className={`text-sm flex items-start gap-2 ${
                              choice.is_correct ? 'text-[#10B981]' : 'text-slate-300'
                            }`}>
                              <span className="mt-0.5">{choice.is_correct ? '✓' : '○'}</span>
                              {choice.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Open question expected answer */}
                    {generatedData.expected_answer && generatedData.expected_answer.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">
                          Réponse attendue
                        </p>
                        <ul className="space-y-1">
                          {generatedData.expected_answer.map((answer, i) => (
                            <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                              <span className="text-[#10B981] mt-0.5">✓</span>
                              {answer}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {generatedData.tags && generatedData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {generatedData.tags.map((tag, i) => (
                          <span key={i} className="px-2 py-1 bg-[#0B132B] text-xs text-slate-400 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4 mt-4">
                    <button
                      onClick={() => setGeneratedData(null)}
                      className="eva-btn-secondary"
                      data-testid="modal-regenerate-button"
                    >
                      Régénérer
                    </button>
                    <button
                      onClick={handleSaveQuestion}
                      disabled={saving}
                      className="eva-btn-primary"
                      data-testid="modal-save-button"
                    >
                      {saving ? 'Enregistrement...' : 'Enregistrer la question'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
